import { useEffect, useState } from "react"
import { fetchEntity } from "@/lib/entity-api"
import { buildFullAddress, fetchDailyWeatherForRange, geocodeAddressToCoords } from "../../daily-logs/weatherAPI"

/**
 * Today's forecast for the project, reusing the exact same weather source
 * Calendar View already uses (daily-logs/weatherAPI.ts) -- no second
 * integration. Shown compactly in the Gantt toolbar; a future
 * weather-vs-schedule alert agent (documented as pending, spec #9) would
 * build on this same data source, not a new one.
 */
export function GanttWeatherBadge({ projectId, companyId }: { projectId?: string; companyId?: string | null }) {
  const [today, setToday] = useState<{ emoji: string; temp: string } | null>(null)

  useEffect(() => {
    let cancelled = false
    if (!projectId || !companyId) {
      setToday(null)
      return
    }
    ;(async () => {
      const data = await fetchEntity<{ address?: string; city?: string; state?: string; zip_code?: string; latitude?: number; longitude?: number }>("project", projectId).catch(() => null)
      if (cancelled || !data) return

      let coords: { latitude: number; longitude: number } | null = null
      if (typeof data.latitude === "number" && typeof data.longitude === "number") {
        coords = { latitude: data.latitude, longitude: data.longitude }
      } else {
        const fullAddress = buildFullAddress([data.address, data.city, data.state, data.zip_code])
        if (!fullAddress) return
        coords = await geocodeAddressToCoords(fullAddress)
      }
      if (cancelled || !coords) return

      const now = new Date()
      const summaries = await fetchDailyWeatherForRange(coords, now, now)
      if (cancelled || summaries.length === 0) return
      const s = summaries[0]
      const highF = Number.isFinite(s.highC) ? Math.round((s.highC * 9) / 5 + 32) : null
      const lowF = Number.isFinite(s.lowC) ? Math.round((s.lowC * 9) / 5 + 32) : null
      setToday({ emoji: s.emoji, temp: highF !== null && lowF !== null ? `${lowF}°/${highF}°F` : "" })
    })()
    return () => {
      cancelled = true
    }
  }, [projectId, companyId])

  if (!today) return null

  return (
    <div className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-600" title="Today's weather at the project site">
      <span>{today.emoji}</span>
      <span>{today.temp}</span>
    </div>
  )
}
